import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/layout/Header";
import { ChevronRight } from "lucide-react";
import { getSortedPosts as getFallbackPosts, type BlogPost } from "@/content/blog/posts";
import { listPosts } from "@/lib/blog";
import { SITE_URL } from "@/config/site";
import { BlogCard } from "@/components/blog/BlogCard";

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>(() => getFallbackPosts());

  useEffect(() => {
    listPosts({ publishedOnly: true })
      .then((rows) => { if (rows.length) setPosts(rows); })
      .catch((e) => console.warn("Blog: falling back to local posts", e));
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "BabbleBlog",
      url: `${SITE_URL}/blog`,
      blogPost: posts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        description: p.description,
        datePublished: p.date,
        url: `${SITE_URL}/blog/${p.slug}`,
        articleSection: p.category,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "BabbleBlog", item: `${SITE_URL}/blog` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fbf9f4] text-slate-900 flex flex-col text-left">
      <SEO
        title="BabbleBlog – Word Games, Puzzles & Cognitive Health"
        description="Articles from the BabbleGram team on daily word games, Wordle alternatives, the history of word puzzles, and the cognitive benefits of playing every day."
        path="/blog"
        jsonLd={jsonLd}
      />
      <Header />

      <main className="flex-1 pt-20 pb-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <nav aria-label="Breadcrumb" className="pt-6 text-xs text-slate-500">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-[#de0046] hover:underline underline-offset-2">Home</Link></li>
              <li aria-hidden="true"><ChevronRight size={12} /></li>
              <li aria-current="page" className="text-slate-700 font-medium">BabbleBlog</li>
            </ol>
          </nav>

          <header className="pt-10 md:pt-14 pb-8 md:pb-10 border-b border-slate-200">
            <p className="text-xs uppercase tracking-[0.22em] text-[#de0046] font-bold mb-3">
              BabbleBlog
            </p>
            <h1 className="font-serif text-4xl md:text-5xl leading-[1.1] tracking-tight text-slate-900 max-w-3xl">
              Notes on word games, daily puzzles, and the habits behind them.
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Writing from the BabbleGram team on Wordle alternatives, the
              history of word puzzles, and the cognitive science of playing
              every day.
            </p>
          </header>

          <section aria-label="All articles" className="pt-10 md:pt-12">
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {posts.map((post, i) => (
                <li key={post.slug}>
                  <BlogCard post={post} eager={i === 0} />
                </li>
              ))}
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Blog;
